import React, { useEffect, useState } from 'react';
import {
  PrimaryTableRowText,
  TableColumnTitle,
} from '../../../../shared/globalStyling/styledText';
import { Button, Grid, message, Space, Table, Tag, Tooltip, Badge, Typography } from 'antd';
import {
  TagOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  UserAddOutlined,
  FundOutlined,
} from '@ant-design/icons';
import { formatWholeNumberNaN } from '../../../../../core/utils/campaigns';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import TagsPagination from './TagsPagination';
import TagMobileCard from './TagMobileCard';
import CampaignsDrawer from './CampaignsDrawer';
import { dateFormat } from '../../../Tasks/configs/designTokens';
import '../../styles/index.scss';

const { useBreakpoint } = Grid;
const { Text } = Typography;

// Label-to-icon mapping for measured events (exported for reuse by other tag subtabs)
export const labelIcons = {
  'Page Views': <EyeOutlined />,
  'Transactions': <ShoppingCartOutlined />,
  'Sessions': <ClockCircleOutlined />,
  'Sign-Ups': <UserAddOutlined />,
  'Basket Items': <ShoppingOutlined />,
  'Impressions': <FundOutlined />,
};

// Compact metric indicator for main table cells (exported for reuse by other tag subtabs)
export const MetricCard = ({ label, value, date, status }) => {
  const modifier =
    status === 'success' ? '--success' : status === 'error' ? '--error' : '--default';

  return (
    <Tooltip title={date ? `Last updated: ${date}` : 'Signal Unknown'}>
      <div className={`tags-table-metric tags-table-metric${modifier}`}>
        <Text className="tags-table-metric__label">
          {labelIcons[label] && (
            <span className="tags-table-metric__label-icon">{labelIcons[label]}</span>
          )}
          <span className="tags-table-metric__label-text">{label}</span>
        </Text>
        <Text strong className="tags-table-metric__value">
          {labelIcons[label]}
          {formatWholeNumberNaN(value)}
        </Text>
      </div>
    </Tooltip>
  );
};

// Get status based on date and value
const getMetricStatus = (date, value) => {
  if (!date) return 'default';
  if (value < 1) return 'error';
  return 'success';
};

const MainTable = ({ tags = [], history, testTag, isAttentionSignals }) => {
  const screens = useBreakpoint();
  const isCardView = !screens.xl; // Cards below 1200px (laptop + tablet + mobile)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [visibleCount, setVisibleCount] = useState(10);
  const LOAD_MORE_BATCH = 10;

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(tags.length / pageSize));
    if (currentPage > maxPage) setCurrentPage(1);
    if (visibleCount > tags.length && tags.length > 0) setVisibleCount(10);
  }, [tags, pageSize, currentPage, visibleCount]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedTag, setSelectedTag] = useState({ appId: null, name: '' });

  const handleOpenDrawer = (record) => {
    setSelectedTag({ appId: record.appId, name: record.name });
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Tag Name'} />,
      key: 'name',
      width: 280,
      render: (record) => (
        <div className="tags-main__name-cell">
          <PrimaryTableRowText text={record.name} />
          {record.advertiser && (
            <Text type="secondary" className="tags-main__advertiser">
              {record.advertiser}
            </Text>
          )}
        </div>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    isAttentionSignals
      ? {
          title: <TableColumnTitle text={'Funnels'} />,
          key: 'funnels',
          width: 100,
          align: 'center',
          render: (record) => (
            <Badge
              count={record.funnels}
              className={record.funnels > 0 ? 'tags-main__funnels-badge--active' : 'tags-main__funnels-badge--inactive'}
              showZero
            />
          ),
          sorter: (a, b) => a.measuredEvents.funnels - b.measuredEvents.funnels,
        }
      : {},
    {
      title: <TableColumnTitle text={'Measured Events'} />,
      key: 'measuredEvents',
      width: 420,
      render: (record) => {
        const { transactions, pageViews, signups, date, isSignUps, sessions } =
          record.measuredEvents;

        const formattedDate = date ? moment(date).format(dateFormat.full) : null;

        return (
          <Space className="tags-main__events-wrap">
            {pageViews !== undefined && (
              <MetricCard
                label="Page Views"
                value={pageViews}
                date={formattedDate}
                status={getMetricStatus(date, pageViews)}
              />
            )}
            {transactions !== undefined && (
              <MetricCard
                label="Transactions"
                value={transactions}
                date={formattedDate}
                status={getMetricStatus(date, transactions)}
              />
            )}
            {(isSignUps || signups > 0) && (
              <MetricCard
                label="Sign-Ups"
                value={signups}
                date={formattedDate}
                status={getMetricStatus(date, signups)}
              />
            )}
            {sessions > 0 && (
              <MetricCard
                label="Sessions"
                value={sessions}
                date={formattedDate}
                status={getMetricStatus(date, sessions)}
              />
            )}
          </Space>
        );
      },
      sorter: (a, b) => a.measuredEvents.pageViews - b.measuredEvents.pageViews,
    },
    {
      title: <TableColumnTitle text={'Tag ID'} />,
      key: 'appId',
      width: 340,
      render: (record) => (
        <Typography.Text
          copyable={{
            text: record.appId,
            tooltips: ['Copy Tag ID', 'Copied!'],
          }}
          className="tags-main__tag-id"
          onClick={(event) => event.stopPropagation()}
        >
          {record.appId}
        </Typography.Text>
      ),
      sorter: (a, b) => a.appId.localeCompare(b.appId),
    },
    {
      title: <TableColumnTitle text={'Action'} />,
      key: 'action',
      width: 180,
      render: (record) => (
        <div className="tags-main__action-cell">
          <Button
            type="primary"
            icon={<TagOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              testTag({
                appId: record.appId,
                orgId: record.orgId,
                tagId: record.id,
              });
              message.info(
                `Signal Tag Test for ${record.name} initiated. May take a couple of minutes to complete.`
              );
            }}
          >
            Test Signal
          </Button>
          {record.appId && (
            <Button
              type="default"
              icon={<EyeOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                handleOpenDrawer(record);
              }}
              className="tags-main__view-btn"
            >
              View Campaigns
            </Button>
          )}
        </div>
      ),
    },
  ];

  const paginatedTags = tags.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleTestSignal = (record) => {
    testTag({
      appId: record.appId,
      orgId: record.orgId,
      tagId: record.id,
    });
    message.info(
      `Signal Tag Test for ${record.name} initiated. May take a couple of minutes to complete.`
    );
  };

  const mobileVisibleTags = tags.slice(0, visibleCount);
  const hasMore = visibleCount < tags.length;

  return (
    <>
      {isCardView ? (
        <>
          <div className="tags-mobile-card-list">
            {mobileVisibleTags.map((record) => (
              <TagMobileCard
                key={record.id}
                record={record}
                type="measurement"
                onClick={() => history.push(`/tags/details/${record.id}?source=measurement`)}
                onTestSignal={handleTestSignal}
                isAttentionSignals={isAttentionSignals}
                onViewCampaigns={() => handleOpenDrawer(record)}
              />
            ))}
          </div>
          {hasMore && (
            <div className="tags-mobile-load-more">
              <Button
                onClick={() => setVisibleCount((prev) => prev + LOAD_MORE_BATCH)}
                block
              >
                Load More ({tags.length - visibleCount} remaining)
              </Button>
            </div>
          )}
        </>
      ) : (
        <>
          <Table
            aria-label="Measurement tags"
            dataSource={paginatedTags}
            columns={columns.filter((col) => Object.keys(col).length > 0)}
            rowKey={(record) => record.id}
            onRow={(record) => ({
              onClick: () => {
                history.push(`/tags/details/${record.id}?source=measurement`);
              },
            })}
            scroll={{ x: 1320 }}
            size="middle"
            pagination={false}
          />
          <TagsPagination
            current={currentPage}
            pageSize={pageSize}
            total={tags.length}
            onChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}
      <CampaignsDrawer
        visible={drawerVisible}
        onClose={handleCloseDrawer}
        tagId={selectedTag.appId}
        tagName={selectedTag.name}
      />
    </>
  );
};

export default withRouter(MainTable);
